import { createAsyncThunk } from '@reduxjs/toolkit'
import type { AxiosInstance } from 'axios'
import {
  broadcastLogout,
  clearAccessToken,
  getAccessToken,
  refreshAccessToken,
  setAccessToken,
} from './authStore'

type AuthApi = Pick<AxiosInstance, 'get' | 'post'>

type CreateAuthThunksOptions = {
  api: AuthApi
  userType: string
  typePrefix: string
  fetchActionName?: string
}

function errorMessage(error: any, fallback: string) {
  return error?.response?.data?.error || fallback
}

async function getCurrentProfile(api: AuthApi) {
  const response = await api.get('/users/me')
  return response.data?.user || response.data
}

/**
 * Builds the shared session-backed auth thunks. App slices retain their own state and reducers,
 * while login, refresh bootstrap, logout, email confirmation, and magic-link behavior stays
 * identical across every frontend.
 */
export function createAuthThunks({
  api,
  userType,
  typePrefix,
  fetchActionName = 'fetchCurrentUser',
}: CreateAuthThunksOptions) {
  const login = createAsyncThunk(
    `${typePrefix}/login`,
    async (credentials: { email: string; password: string }, { rejectWithValue }) => {
      try {
        const response = await api.post('/users/login', { ...credentials, user_type: userType })
        if (response.data?.token) {
          setAccessToken(response.data.token)
        }
        return await getCurrentProfile(api)
      } catch (error: any) {
        if (error?.response?.status === 403 && error?.response?.data?.email_verified === false) {
          return rejectWithValue('Please verify your email address to continue.')
        }
        return rejectWithValue(errorMessage(error, 'Login failed'))
      }
    },
  )

  const fetchCurrentUser = createAsyncThunk(
    `${typePrefix}/${fetchActionName}`,
    async (_, { rejectWithValue }) => {
      const token = getAccessToken() || (await refreshAccessToken())
      if (!token) return null

      try {
        return await getCurrentProfile(api)
      } catch (error: any) {
        return rejectWithValue(errorMessage(error, 'Failed to fetch current user'))
      }
    },
  )

  const logout = createAsyncThunk(`${typePrefix}/logout`, async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/users/logout')
      clearAccessToken()
      broadcastLogout()
      return response.data
    } catch (error: any) {
      clearAccessToken()
      broadcastLogout()
      return rejectWithValue(errorMessage(error, 'Logout failed'))
    }
  })

  const requestMagicLink = createAsyncThunk(
    `${typePrefix}/requestMagicLink`,
    async (email: string, { rejectWithValue }) => {
      try {
        const response = await api.post('/users/request-magic-link', {
          email,
          user_type: userType,
        })
        return response.data
      } catch (error: any) {
        return rejectWithValue(errorMessage(error, 'Failed to send magic link'))
      }
    },
  )

  const verifyMagicLink = createAsyncThunk(
    `${typePrefix}/verifyMagicLink`,
    async (token: string, { rejectWithValue }) => {
      try {
        const response = await api.get(`/users/verify-magic-link/${token}`)
        if (response.data?.token) {
          setAccessToken(response.data.token)
        }
        return await getCurrentProfile(api)
      } catch (error: any) {
        return rejectWithValue(errorMessage(error, 'Invalid or expired magic link'))
      }
    },
  )

  const confirmEmail = createAsyncThunk(
    `${typePrefix}/confirmEmail`,
    async (token: string, { rejectWithValue }) => {
      try {
        const response = await api.post('/users/confirm-email', { token })
        if (response.data?.token) {
          setAccessToken(response.data.token)
        }
        return await getCurrentProfile(api)
      } catch (error: any) {
        return rejectWithValue(errorMessage(error, 'Email confirmation failed'))
      }
    },
  )

  return { login, fetchCurrentUser, logout, requestMagicLink, verifyMagicLink, confirmEmail }
}
